class CustomerGallery extends HTMLElement {
  connectedCallback() {
    this.viewport = this.querySelector('[data-gallery-viewport]');
    this.cards = [...this.querySelectorAll('[data-gallery-card]')];
    this.dots = [...this.querySelectorAll('[data-gallery-dot]')];
    this.previousButton = this.querySelector('[data-gallery-previous]');
    this.nextButton = this.querySelector('[data-gallery-next]');
    this.autoplayEnabled = this.dataset.autoplay === 'true';
    this.autoplayDelay = (Number(this.dataset.autoplaySeconds) || 5) * 1000;

    if (!this.viewport || this.cards.length < 2) return;

    this.previousButton?.addEventListener('click', () => this.handleManualMove(-1));
    this.nextButton?.addEventListener('click', () => this.handleManualMove(1));
    this.dots.forEach((dot, index) => dot.addEventListener('click', () => this.goTo(index)));
    this.viewport.addEventListener('scroll', () => this.queueUpdate(), { passive: true });
    new ResizeObserver(() => this.update()).observe(this.viewport);
    this.update();
    this.addEventListener('mouseenter', () => this.stopAutoplay());
    this.addEventListener('mouseleave', () => this.startAutoplay());
    this.addEventListener('focusin', () => this.stopAutoplay());
    this.addEventListener('focusout', () => this.startAutoplay());
    document.addEventListener('visibilitychange', this.handleVisibilityChange);
    this.startAutoplay();
  }

  disconnectedCallback() {
    this.stopAutoplay();
    document.removeEventListener('visibilitychange', this.handleVisibilityChange);
  }

  handleVisibilityChange = () => {
    if (document.hidden) this.stopAutoplay();
    else this.startAutoplay();
  };

  step() {
    if (this.cards.length < 2) return this.cards[0]?.offsetWidth || 0;
    return this.cards[1].offsetLeft - this.cards[0].offsetLeft;
  }

  currentIndex() {
    const step = this.step();
    return step ? Math.round(this.viewport.scrollLeft / step) : 0;
  }

  goTo(index) {
    this.viewport.scrollTo({ left: this.step() * index, behavior: 'smooth' });
  }

  move(direction) {
    this.goTo(Math.max(0, Math.min(this.cards.length - 1, this.currentIndex() + direction)));
  }

  handleManualMove(direction) {
    this.stopAutoplay();
    this.move(direction);
    this.startAutoplay();
  }

  startAutoplay() {
    this.stopAutoplay();
    if (!this.autoplayEnabled || !matchMedia('(min-width: 750px)').matches || matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    this.autoplayTimer = setInterval(() => {
      const lastStart = Math.max(0, this.cards.length - 5);
      const nextIndex = this.currentIndex() >= lastStart ? 0 : this.currentIndex() + 1;
      this.goTo(nextIndex);
    }, this.autoplayDelay);
  }

  stopAutoplay() {
    clearInterval(this.autoplayTimer);
  }

  queueUpdate() {
    cancelAnimationFrame(this.updateFrame);
    this.updateFrame = requestAnimationFrame(() => this.update());
  }

  update() {
    const index = Math.max(0, Math.min(this.cards.length - 1, this.currentIndex()));
    this.dots.forEach((dot, dotIndex) => {
      const active = dotIndex === index;
      dot.classList.toggle('is-active', active);
      dot.setAttribute('aria-current', String(active));
    });

    if (this.previousButton) this.previousButton.disabled = this.viewport.scrollLeft <= 1;
    if (this.nextButton) {
      const end = this.viewport.scrollWidth - this.viewport.clientWidth - 1;
      this.nextButton.disabled = this.viewport.scrollLeft >= end;
    }
  }
}

if (!customElements.get('customer-gallery')) {
  customElements.define('customer-gallery', CustomerGallery);
}
